/**
 * Rezervace (Hosté → Rezervace) — sdílené konstanty a pomocné funkce.
 *
 * Do 2026-10-06 bydlelo všechno v jediném `pages/Rezervace.jsx`. Data jedou
 * po stejném API jako pokladna (`/api/pos/rezervace`) a po stejných pravidlech:
 * kolize ani překročená kapacita zápis neblokují, jen se vrátí jako varování.
 */

/** Stav rezervace → barva a popisek. Sjednoceno s POSem (`lib/posUi.js`). */
export const STAVY = {
    potvrzena:  { label: 'Potvrzená',  color: '#3B82F6' },
    posazena:   { label: 'Sedí',       color: '#10B981' },
    nedorazila: { label: 'Nedorazili', color: '#F59E0B' },
    zrusena:    { label: 'Zrušená',    color: '#8E8E93' },
};

const csrf = () => document.querySelector('meta[name="csrf-token"]')?.content;

export const api = async (url, method = 'GET', body) => {
    const opts = { method, headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } };
    if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
    const r = await fetch(url, opts);
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw e; }
    return r.json();
};

/** Date → „2026-08-31" v místním čase (toISOString by ujel o časovou zónu). */
export const naDatum = (d) => d.toLocaleDateString('sv-SE');

export const posun = (datum, dnu) => {
    const d = new Date(`${datum}T12:00:00`);
    d.setDate(d.getDate() + dnu);
    return naDatum(d);
};

export const pondeli = (datum) => {
    const d = new Date(`${datum}T12:00:00`);
    return posun(datum, -((d.getDay() + 6) % 7));
};

export const cas = (iso) => (iso ? new Date(iso).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' }) : '—');
export const naCas = (d) => d.toTimeString().slice(0, 5);

/** Posun okamžiku o minuty — vrací dvojici [datum, čas] pro pole formuláře. */
export const posunOMinuty = (den, hodina, minut) => {
    const d = new Date(`${den}T${hodina}:00`);
    d.setMinutes(d.getMinutes() + minut);
    return [naDatum(d), naCas(d)];
};

export const rozdilMinut = (odDen, odCas, doDen, doCas) =>
    Math.round((new Date(`${doDen}T${doCas}:00`) - new Date(`${odDen}T${odCas}:00`)) / 60000);

/** „450" → „7,5 h". Pod hodinu se drží minuty, ať se nezaokrouhluje do nesmyslu. */
export const popisDelky = (minut) => {
    if (!minut || minut < 60) return `${minut || 0} min`;
    const hodin = minut / 60;
    return `${Number.isInteger(hodin) ? hodin : hodin.toFixed(1).replace('.', ',')} h`;
};

/** Rychlé délky. Delší se doťuká v poli „Do" — tohle jsou ty běžné. */
export const DELKY = [60, 90, 120, 180];

export const OPAKOVANI = [
    { id: 'zadne', label: 'Ne' },
    { id: 'denne', label: 'Denně' },
    { id: 'tydne', label: 'Týdně' },
    { id: '14dni', label: 'Po 14 dnech' },
    { id: 'mesicne', label: 'Měsíčně' },
];

/** Kam rezervace míří — „Zahrádka (celá)" / „Stůl 4" / „Stůl 4, 5 a 6". */
export const cil = (r) => {
    if (r.cela_sekce) return `${r.zona?.nazev ?? 'Sekce'} (celá)`;
    const nazvy = (r.stoly || []).map(s => s.nazev || `Stůl ${s.cislo}`);
    if (!nazvy.length) return '—';
    if (nazvy.length === 1) return nazvy[0];
    return `${nazvy.slice(0, -1).join(', ')} a ${nazvy.at(-1)}`;
};

export const denCesky = (datum) =>
    new Date(`${datum}T12:00:00`).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric' });

/** „Úterý 6. října" — velké jen první písmeno (CSS `capitalize` by zvětšil i měsíc) */
export const denDlouze = (datum) => {
    const t = new Date(`${datum}T12:00:00`).toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long' });
    return t.charAt(0).toUpperCase() + t.slice(1);
};

/** „18:00–22:00" / „celý den" / „1. 9. – 3. 9." podle toho, co rezervace je. */
export const oknoPopis = (r) => {
    if (r.cely_den) {
        return r.vice_dnu
            ? `celý den · ${denCesky(r.datum)} – ${denCesky(naDatum(new Date(r.konec)))}`
            : 'celý den';
    }
    return r.vice_dnu
        ? `${cas(r.zacatek)} ${denCesky(r.datum)} – ${cas(r.konec)} ${denCesky(naDatum(new Date(r.konec)))}`
        : `${cas(r.zacatek)}–${cas(r.konec)}`;
};

/**
 * Pokrývá rezervace tenhle den? Vícedenní akce patří na všechny svoje dny.
 * Konec se převádí na místní datum — z ISO řetězce by se uřízl den podle UTC.
 */
export const zasahujeDen = (r, den) =>
    r.datum <= den && (r.konec ? naDatum(new Date(r.konec)) : r.datum) >= den;

/** Hledání v rezervacích — jméno, telefon (i bez mezer), poznámka, stůl. */
export const odpovidaHledani = (r, hledani) => {
    const q = hledani.trim().toLowerCase();
    if (!q) return true;
    const cislice = q.replace(/\D/g, '');
    return `${r.jmeno || ''} ${r.poznamka || ''} ${cil(r)}`.toLowerCase().includes(q)
        || (cislice.length >= 3 && String(r.telefon || '').replace(/\D/g, '').includes(cislice));
};
